import { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Text,
  useToast,
} from "@chakra-ui/react";
import { crm, Pill } from "../../../theme";
import {
  getLpcoForm,
  LpcoField,
  LpcoForm,
  LpcoValue,
  LpcoValues,
  refreshLpco,
  saveLpcoValues,
  submitLpco,
} from "../../../services/govRegistrationService";

const labelProps = { fontSize: "12px", fontWeight: "600", color: crm.textMuted, mb: "4px" };
const inputProps = { h: "42px", borderRadius: "10px", focusBorderColor: crm.brand };

const isEmpty = (v: LpcoValue | undefined): boolean => {
  if (v == null) return true;
  if (typeof v === "string") return v.trim() === "";
  if (Array.isArray(v)) return v.every((x) => String(x).trim() === "");
  return Object.values(v).every((x) => x == null || x === "" || (typeof x === "object" && isEmpty(x as any)));
};

const same = (a: LpcoValue | undefined, b: LpcoValue | undefined) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/** Achata subatributos (renderizados como campos próprios; só o condicionante decide a obrigatoriedade no servidor). */
const flat = (fields: LpcoField[]): LpcoField[] => fields.flatMap((f) => [f, ...flat(f.sub ?? [])]);

interface FieldInputProps {
  field: LpcoField;
  value: LpcoValue | undefined;
  auto: boolean;
  onChange: (v: LpcoValue) => void;
  disabled: boolean;
}

const FieldInput = ({ field, value, auto, onChange, disabled }: FieldInputProps) => {
  const obj = (typeof value === "object" && !Array.isArray(value) ? value : {}) as Record<string, any>;
  const set = (patch: Record<string, any>) => onChange({ ...obj, ...patch });
  let control: JSX.Element;

  if (field.type === "IMPORTACAO_TERCEIROS") {
    const ind = String(obj.codigoIndicador ?? "0");
    control = (
      <Flex gap="8px" wrap="wrap">
        <Select {...inputProps} w="260px" isDisabled={disabled} value={ind} onChange={(e) => set({ codigoIndicador: Number(e.target.value) })}>
          <option value="0">Importação direta</option>
          <option value="1">Por conta e ordem</option>
          <option value="2">Por encomenda</option>
        </Select>
        {ind !== "0" && (
          <Input {...inputProps} w="220px" isDisabled={disabled} placeholder="CPF/CNPJ do adquirente" value={obj.cpfCnpj ?? ""} onChange={(e) => set({ cpfCnpj: e.target.value })} />
        )}
      </Flex>
    );
  } else if (field.type === "FABRICANTE") {
    const pais = String(obj.codigoPais ?? "");
    const op = (obj.operadorEstrangeiro ?? {}) as Record<string, any>;
    const setOp = (patch: Record<string, any>) => set({ conhecido: true, operadorEstrangeiro: { ...op, codigoPais: pais, ...patch } });
    control = (
      <Flex gap="8px" wrap="wrap">
        <Input {...inputProps} w="90px" isDisabled={disabled} placeholder="País (ISO2)" maxLength={2} value={pais}
          onChange={(e) => set({ conhecido: true, codigoPais: e.target.value.toUpperCase(), operadorEstrangeiro: { ...op, codigoPais: e.target.value.toUpperCase() } })} />
        {pais === "BR" ? (
          <Input {...inputProps} w="220px" isDisabled={disabled} placeholder="CPF/CNPJ do fabricante" value={obj.cpfCnpj ?? ""} onChange={(e) => set({ cpfCnpj: e.target.value })} />
        ) : (
          <>
            <Input {...inputProps} w="190px" isDisabled={disabled} placeholder="Código do operador" value={op.codigo ?? ""} onChange={(e) => setOp({ codigo: e.target.value })} />
            <Input {...inputProps} w="130px" isDisabled={disabled} placeholder="CNPJ raiz" maxLength={8} value={op.cpfCnpjRaiz ?? ""} onChange={(e) => setOp({ cpfCnpjRaiz: e.target.value })} />
            <Input {...inputProps} w="80px" isDisabled={disabled} placeholder="Versão" value={op.versao ?? ""} onChange={(e) => setOp({ versao: e.target.value })} />
          </>
        )}
      </Flex>
    );
  } else if (field.domains.length > 0) {
    const single = Array.isArray(value) ? value[0] ?? "" : typeof value === "string" ? value : "";
    control = (
      <Select {...inputProps} isDisabled={disabled} placeholder="Selecione" value={single} onChange={(e) => onChange(e.target.value)}>
        {field.domains.map((d) => (
          <option key={d.id} value={d.id}>
            {d.description || d.id}
          </option>
        ))}
      </Select>
    );
  } else {
    const text = Array.isArray(value) ? value.join(", ") : typeof value === "string" ? value : "";
    control = (
      <Input
        {...inputProps}
        isDisabled={disabled}
        value={text}
        maxLength={field.maxLength ?? undefined}
        placeholder={field.mask ? `Formato ${field.mask}` : undefined}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  return (
    <Box mb="14px">
      <Flex align="center" gap="8px" mb="4px">
        <Text {...labelProps} mb="0">
          {field.name}
          {field.required ? " *" : ""}
        </Text>
        {auto && <Pill color={crm.brand}>Auto</Pill>}
      </Flex>
      {control}
    </Box>
  );
};

/** Preenche e envia um LPCO (modelo dinâmico do TALPCO) a partir do processo. Só equipe interna. */
const LpcoFormModal = ({
  isOpen,
  onClose,
  processId,
  modelCode,
  onDone,
}: {
  isOpen: boolean;
  onClose: () => void;
  processId: string;
  modelCode: string;
  onDone: () => Promise<unknown> | void;
}) => {
  const toast = useToast();
  const [form, setForm] = useState<LpcoForm | null>(null);
  const [values, setValues] = useState<LpcoValues>({ form: {}, items: {} });
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showPayload, setShowPayload] = useState(false);
  const initial = useRef<LpcoValues>({ form: {}, items: {} });

  const apply = (f: LpcoForm) => {
    setForm(f);
    const v = { form: { ...(f.values?.form ?? {}) }, items: JSON.parse(JSON.stringify(f.values?.items ?? {})) } as LpcoValues;
    setValues(v);
    initial.current = JSON.parse(JSON.stringify(v));
  };

  const load = async () => {
    setLoading(true);
    try {
      apply(await getLpcoForm(processId, modelCode));
    } catch {
      toast({ title: "Falha ao carregar o modelo de LPCO", status: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) load();
  }, [isOpen, processId, modelCode]);

  const locked = !!form?.lpcoNumber;

  // só o que difere do automático (o resto é recalculado a cada abertura)
  const userValues = (): LpcoValues => {
    const auto = form?.auto ?? { form: {}, items: {} };
    const out: LpcoValues = { form: {}, items: {} };
    Object.entries(values.form).forEach(([k, v]) => {
      if (!isEmpty(v) && !same(v, auto.form?.[k])) out.form[k] = v;
    });
    Object.entries(values.items).forEach(([n, o]) => {
      Object.entries(o).forEach(([k, v]) => {
        if (!isEmpty(v) && !same(v, auto.items?.[n]?.[k])) (out.items[n] ??= {})[k] = v;
      });
    });
    return out;
  };

  const save = async () => {
    setBusy(true);
    try {
      const f = await saveLpcoValues(processId, modelCode, userValues());
      if (f.success) {
        setForm(f);
        toast({ title: "Rascunho salvo", status: "success", duration: 3000 });
      } else toast({ title: "Não salvo", description: f.error, status: "error" });
    } catch {
      toast({ title: "Erro ao salvar", status: "error" });
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    setBusy(true);
    try {
      const r = await submitLpco(processId, modelCode, userValues());
      toast({
        title: r.success ? "LPCO solicitado" : "Não solicitado",
        description: r.messages.join("\n"),
        status: r.success ? "success" : "error",
        duration: 15000,
        isClosable: true,
      });
      await onDone();
      await load();
    } catch (e: any) {
      toast({ title: "Erro ao solicitar LPCO", description: e?.response?.data?.messages?.join?.("\n"), status: "error", duration: 12000, isClosable: true });
    } finally {
      setBusy(false);
    }
  };

  const refresh = async () => {
    setBusy(true);
    try {
      const r = await refreshLpco(processId, modelCode);
      toast({ title: r.success ? "Situação atualizada" : "Falha", description: r.messages.join("\n"), status: r.success ? "success" : "error", duration: 8000 });
      await onDone();
      await load();
    } finally {
      setBusy(false);
    }
  };

  const model = form?.model;
  const issues = form?.issues ?? [];
  const blocking = issues.filter((i) => i.blocking);
  const formFields = useMemo(() => flat(model?.formFields ?? []), [model]);
  const ncmFields = useMemo(() => flat(model?.ncmFields ?? []), [model]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="3xl" scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(2px)" />
      <ModalContent borderRadius="16px" boxShadow={crm.shadowLg}>
        <ModalHeader borderBottom={`1px solid ${crm.border}`}>
          <Text fontSize="18px" fontWeight="800" color={crm.text}>
            LPCO {modelCode.toUpperCase()}
            {model?.organ ? ` · ${model.organ}` : ""}
          </Text>
          <Text fontSize="13px" fontWeight="500" color={crm.textMuted}>
            {model?.name ?? "Carregando modelo…"}
          </Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody py="20px">
          {loading && !form && <Spinner />}
          {form && !form.success && (
            <Text color={crm.danger} fontSize="13px">
              {form.error}
            </Text>
          )}
          {form?.success && model && (
            <>
              {form.lpcoNumber && (
                <Box mb="14px" p="12px" borderRadius="10px" bg="#f0fdf4" border="1px solid #bbf7d0">
                  <Text fontSize="13px" fontWeight="700">
                    LPCO {form.lpcoNumber} · {form.status}
                  </Text>
                </Box>
              )}
              {model.info && (
                <Text fontSize="12px" color="#a16207" mb="14px">
                  {model.info}
                </Text>
              )}
              <Text fontSize="13px" fontWeight="800" color={crm.text} mb="10px">
                Dados gerais
              </Text>
              {formFields.map((f) => (
                <FieldInput
                  key={f.code}
                  field={f}
                  value={values.form[f.code]}
                  auto={!!form.auto?.form?.[f.code] && same(values.form[f.code], form.auto.form[f.code])}
                  disabled={locked}
                  onChange={(v) => setValues((s) => ({ ...s, form: { ...s.form, [f.code]: v } }))}
                />
              ))}
              {form.items.map((it) => (
                <Box key={it.number} mt="18px" pt="14px" borderTop={`1px solid ${crm.border}`}>
                  <Text fontSize="13px" fontWeight="800" color={crm.text} mb="2px">
                    Item {it.number} · NCM {it.ncm}
                  </Text>
                  <Text fontSize="12px" color={crm.textMuted} mb="10px">
                    {it.description} · Produto Catálogo: {it.product ?? "não cadastrado"}
                  </Text>
                  {ncmFields.map((f) => (
                    <FieldInput
                      key={f.code}
                      field={f}
                      value={values.items[String(it.number)]?.[f.code]}
                      auto={!!form.auto?.items?.[String(it.number)]?.[f.code] && same(values.items[String(it.number)]?.[f.code], form.auto.items[String(it.number)][f.code])}
                      disabled={locked}
                      onChange={(v) =>
                        setValues((s) => ({ ...s, items: { ...s.items, [String(it.number)]: { ...(s.items[String(it.number)] ?? {}), [f.code]: v } } }))
                      }
                    />
                  ))}
                </Box>
              ))}
              {issues.length > 0 && !locked && (
                <Box mt="16px">
                  {issues.map((i, idx) => (
                    <Text key={idx} fontSize="12px" color={i.blocking ? "#b91c1c" : "#a16207"}>
                      • <b>{i.field}:</b> {i.message}
                    </Text>
                  ))}
                  <Text fontSize="12px" color={crm.textFaint} mt="4px">
                    Pendências refletem o último "Salvar rascunho".
                  </Text>
                </Box>
              )}
              <Button size="xs" variant="ghost" mt="12px" onClick={() => setShowPayload((v) => !v)}>
                {showPayload ? "Ocultar payload" : "Ver payload"}
              </Button>
              {showPayload && form.payload && (
                <Box as="pre" mt="8px" p="12px" fontSize="12px" bg="#f8fafc" borderRadius="8px" overflow="auto" maxH="260px">
                  {form.payload}
                </Box>
              )}
            </>
          )}
        </ModalBody>
        <ModalFooter borderTop={`1px solid ${crm.border}`} gap="8px">
          <Button variant="ghost" onClick={onClose}>
            Fechar
          </Button>
          {locked ? (
            <Button colorScheme="blue" onClick={refresh} isLoading={busy}>
              Atualizar situação
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={save} isLoading={busy} isDisabled={!form?.success}>
                Salvar rascunho
              </Button>
              <Button colorScheme="blue" onClick={submit} isLoading={busy} isDisabled={!form?.success}>
                {blocking.length > 0 ? "Enviar (há pendências)" : "Enviar ao Portal"}
              </Button>
            </>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default LpcoFormModal;
